import { ProductLabel } from "../../ui/product-label.tsx";
import { Link, useParams } from "@tanstack/react-router";
import { ClockIcon, TerminalWindowIcon } from "@phosphor-icons/react";
import { INBOX_LABELS as I } from "../../labels.ts";
import { titleOf } from "../../inbox-items.ts";
import { LauncherRun } from "../../local-launcher.tsx";
import { Callout, CopyButton, Pill } from "../../ui.tsx";
import { AgentLogo } from "../../ui/props.tsx";
import { useInbox, Review, DetailPanelShell } from "./shared.tsx";

// Part of the inbox (docs/design-system.md §0.4, plan M7 step 7g).

/** The run commands for both hosts; the agent does the work, zooi only asks. */
function RunCommands({ productId }: { productId: string }) {
  return <section className="z-notice-run">
    <LauncherRun key={productId} productId={productId} />
    <details className="z-disclosure z-run-manual">
      <summary><TerminalWindowIcon size={16} aria-hidden /><span>{I.runManually}</span></summary>
      <div className="z-flow">
        <p className="z-small z-muted">{I.runWhere}</p>
        <div className="z-run-commands">
          <div><span className="z-executor"><AgentLogo engine="claude_code" />{I.claudeCode}</span><code>/zooi:run</code><CopyButton text="/zooi:run" /></div>
          <div><span className="z-executor"><AgentLogo engine="codex" />{I.codex}</span><code>$zooi:run</code><CopyButton text="$zooi:run" /></div>
        </div>
      </div>
    </details>
  </section>;
}

/** Runs, missed runs, stopped off-cycle requests, expiring connections and refused repository tokens: told from the inbox item itself. */
export function NoticeReview() {
  const { noticeKey } = useParams({ strict: false }) as { noticeKey: string };
  const { items } = useInbox();
  const item = items.find((entry) => entry.key === decodeURIComponent(noticeKey));
  if (!item) return <DetailPanelShell><Callout>{I.closed}</Callout></DetailPanelShell>;
  const reasons = item.facts.reasons ?? [];
  const discussions = reasons.filter((code) => code === "discussion_expiring").length;
  const releases = item.facts.releases ?? [];
  const lines = [...new Set(reasons.filter((code) => code !== "discussion_expiring"))].map((code) => code === "release_published" && releases.length ? I.releasesPublished(releases) : I.runReason[code] ?? code);
  if (discussions === 1) lines.push(I.runReason.discussion_expiring);
  else if (discussions > 1) lines.push(I.discussionsExpiring(discussions));
  return <Review label={I.detail} title={titleOf(item)}
    meta={<><Pill tone={item.kind === "run_request" && item.reason.code === "run_requested" ? "teal" : "amber"} status>{I.reason[item.reason.code] ?? item.reason.code}</Pill><ProductLabel product={item.product} />{item.due_at && <span>{item.kind === "run_request" ? I.dueBy(item.due_at) : I.expires(item.due_at)}</span>}{(item.facts.reminders ?? 0) > 0 && <span>{I.reminded(item.facts.reminders!)}</span>}</>}>
    {item.kind === "run_request" && lines.length > 0 && <div className="z-notice-reason"><ClockIcon size={18} aria-hidden /><ul className="z-points" role="list">{lines.map((line) => <li key={line}>{line}</li>)}</ul></div>}
    {item.kind === "run_missed" && <p>{I.missedNote}</p>}
    {item.kind === "runs_degraded" && <p>{I.degradedNote(Number(item.reason.params?.count ?? 3))}</p>}
    {item.kind === "connection" && <p>{I.connectionNote}</p>}
    {item.kind === "connection_failing" && <p>{I.connectionFailingNote}</p>}
    {item.kind === "feedback" && <p>{I.feedbackNote}</p>}
    {item.kind === "plans_idle" && <p>{I.plansIdleNote}</p>}
    {item.kind === "product_idle" && <p>{I.productIdleNote}</p>}
    {item.kind === "run_stalled" && <p>{I.runStalledNote}</p>}
    {item.kind === "merge" && <p>{I.mergeNote}</p>}
    {item.kind === "connection"
      ? <p><Link to="/settings/tokens">{I.manageTokens}</Link></p>
      : item.kind === "connection_failing"
      ? <p><Link to="/p/$productId/profile" params={{ productId: item.product.id }} search={{ view: "sources" }} hash="product-sources">{I.giveNewToken}</Link></p>
      : item.kind === "feedback" || item.kind === "run_stalled" || item.kind === "merge"
      ? <p className="z-inline">
        {item.facts.url && <a className="z-link" href={item.facts.url} target="_blank" rel="noreferrer">{item.kind === "merge" ? I.openPullRequest : I.openComment}</a>}
        {item.facts.action_id && <Link className="z-link" to="/p/$productId/actions/$actionId" params={{ productId: item.product.id, actionId: item.facts.action_id }}>{I.openAction}</Link>}
      </p>
      : item.kind === "plans_idle" || item.kind === "product_idle"
      ? <p><Link className="z-link" to="/p/$productId/week" params={{ productId: item.product.id }}>{I.openWeek}</Link></p>
      : <><RunCommands productId={item.product.id} /><p className="z-notice-settings"><Link to="/settings/products/$productId/agent" params={{ productId: item.product.id }} hash="run-settings"><ClockIcon size={14} aria-hidden />{I.runSettingsLink}</Link></p></>}
  </Review>;
}
